/**
 * Minimaler HTML-Sanitizer für Fremd-Inhalte (Pipedrive-Notizen, Aktivitäts-Texte).
 *
 * Warum: Notizen enthalten Daten, die ursprünglich aus öffentlichen Formularen stammen
 * (z. B. der Vorname eines Facebook-Leads). Würde man sie ungefiltert über
 * dangerouslySetInnerHTML rendern, könnte ein beliebiger Besucher der Landingpage
 * Skript-Code im eingeloggten Admin-Browser ausführen und das JWT aus dem
 * sessionStorage abgreifen.
 *
 * Ansatz: Whitelist. Erlaubt sind nur harmlose Formatierungs-Tags, alles andere wird
 * zu Text entschärft. Attribute werden bis auf href (nur http/https/mailto/tel) entfernt,
 * damit weder Event-Handler (onerror, onclick, …) noch javascript:-URLs durchkommen.
 */

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV', 'SPAN', 'UL', 'OL', 'LI', 'A']);
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

export function sanitizeHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  // Parsen in einem inerten Dokument: Bilder/Skripte werden dabei nicht geladen
  // und Event-Handler nicht registriert.
  const doc = document.implementation.createHTMLDocument('sanitize');
  const root = doc.body;
  root.innerHTML = dirty;

  const walk = (node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) continue; // Text — unbedenklich
      if (child.nodeType !== 1) { child.remove(); continue; } // Kommentare etc.

      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Tag nicht erlaubt: Inhalt behalten, Element selbst auflösen.
        // script/style-Inhalte sind kein Text, den man sehen will — komplett weg.
        if (child.tagName === 'SCRIPT' || child.tagName === 'STYLE') { child.remove(); continue; }
        const text = doc.createTextNode(child.textContent || '');
        child.replaceWith(text);
        continue;
      }

      // Erlaubtes Tag: alle Attribute entfernen, nur sicheres href überlebt.
      for (const attr of Array.from(child.attributes)) {
        const keep = child.tagName === 'A' && attr.name.toLowerCase() === 'href' && SAFE_URL.test(attr.value.trim());
        if (!keep) child.removeAttribute(attr.name);
      }
      if (child.tagName === 'A') {
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
      walk(child);
    }
  };
  walk(root);
  return root.innerHTML;
}

export default sanitizeHtml;
